// ============================================================================
// IDEATION SURVEY FORM
// 4-step wizard matching the n8n ai-suggestion-box form questions.
// Steps: Problem Understanding → Impact & Value → Feasibility & Data → Your Idea
// Redesigned with Stitch card-style inputs and progress indicator.
// ============================================================================

import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Send, Loader2, CheckCircle } from 'lucide-react';

interface IdeationSurveyFormProps {
  onSubmit: (answers: Record<string, any>) => Promise<void>;
  onCancel: () => void;
  submitting: boolean;
}

const STEPS = [
  { title: 'Problem', fullTitle: 'Problem Understanding', fields: ['q1', 'q2', 'q3', 'q4', 'q5'] },
  { title: 'Impact', fullTitle: 'Impact & Value', fields: ['q6', 'q7', 'q8'] },
  { title: 'Feasibility', fullTitle: 'Feasibility & Data', fields: ['q9', 'q10', 'q11', 'q12'] },
  { title: 'Your Idea', fullTitle: 'Your Idea & Details', fields: ['q13', 'q14', 'q15', 'submitterName', 'submitterTeam', 'submitterEmail'] },
];

const FREQUENCY_OPTIONS = ['Multiple times a day', 'Daily', 'Weekly', 'Monthly', 'Occasionally'];
const IMPACTED_OPTIONS = ['Our customers', 'My team', 'Other teams', 'Senior leadership', 'External partners', 'Multiple teams'];
const TIME_OPTIONS = ['Less than 1 hour', '1-5 hours', '5-10 hours', '10+ hours'];
const BENEFITS_OPTIONS = ['Time savings', 'Cost reduction', 'Improved quality', 'Better customer experience', 'Compliance/risk reduction', 'Innovation/competitive advantage'];
const VALUE_OPTIONS = [
  { value: 'Low (nice to have)', label: 'Low', desc: 'Minor efficiency gains' },
  { value: 'Moderate (improves efficiency)', label: 'Moderate', desc: 'Clear ROI and improvement' },
  { value: 'High (business-critical)', label: 'High', desc: 'Business-critical transformation' },
];
const DIGITAL_RECORDS_OPTIONS = ['Yes', 'No', 'Not sure'];
const DATA_LOCATION_OPTIONS = ['Internal systems (CRM, ERP)', 'Excel/spreadsheets', 'Emails/documents', 'Service logs', "I don't know"];
const INPUT_TYPES_OPTIONS = ['Text (emails, reports)', 'Numbers/tables', 'Images/videos', 'Voice/audio'];
const SIMILAR_SOLUTION_OPTIONS = ['Yes', 'No', 'Not sure'];
const STRATEGIC_OPTIONS = ['Revenue growth', 'Cost optimisation', 'Customer satisfaction', 'Employee productivity', 'Digital transformation', 'Risk & compliance'];

const CheckboxGroup: React.FC<{ field: string; options: string[]; answers: Record<string, any>; onToggle: (field: string, opt: string) => void }> = ({ field, options, answers, onToggle }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
    {options.map(opt => {
      const checked = (answers[field] || []).includes(opt);
      return (
        <label
          key={opt}
          className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
            checked
              ? 'border-primary bg-primary/5'
              : 'border-slate-200 hover:border-slate-300'
          }`}
        >
          <input
            type="checkbox"
            checked={checked}
            onChange={() => onToggle(field, opt)}
            className="rounded border-slate-300 text-primary focus:ring-primary w-5 h-5"
          />
          <span className={`text-sm font-medium ${checked ? 'text-[#052831]' : 'text-slate-600'}`}>{opt}</span>
        </label>
      );
    })}
  </div>
);

export const IdeationSurveyForm: React.FC<IdeationSurveyFormProps> = ({ onSubmit, onCancel, submitting }) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, any>>({
    q4: [], q6: [], q10: [], q11: [], q15: []
  });

  const set = (key: string, value: any) => setAnswers(prev => ({ ...prev, [key]: value }));

  const toggleMulti = (key: string, value: string) => {
    setAnswers(prev => {
      const arr: string[] = prev[key] || [];
      return { ...prev, [key]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
    });
  };

  const canAdvance = (): boolean => {
    if (step === 0) return !!(answers.q1 && answers.q2 && answers.q3 && (answers.q4 || []).length > 0 && answers.q5);
    if (step === 1) return !!((answers.q6 || []).length > 0 && answers.q8);
    if (step === 2) return !!(answers.q9 && (answers.q11 || []).length > 0);
    if (step === 3) return !!(answers.q13 && answers.submitterEmail);
    return false;
  };

  const handleSubmit = () => {
    if (!canAdvance()) return;
    onSubmit(answers);
  };

  const inputCls = 'w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all';
  const labelCls = 'block text-base font-bold text-[#052831] mb-2';
  const optionalCls = 'text-slate-400 font-normal text-sm';

  return (
    <div className="space-y-8">
      {/* Step progress indicator */}
      <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-slate-400">
        {STEPS.map((s, i) => (
          <React.Fragment key={i}>
            <button
              onClick={() => i < step && setStep(i)}
              className={`flex items-center gap-2 ${
                i < step ? 'text-primary cursor-pointer' :
                i === step ? 'text-primary' : 'text-slate-400'
              }`}
            >
              {i < step ? (
                <CheckCircle size={18} className="text-primary" />
              ) : (
                <span className={`w-5 h-5 flex items-center justify-center rounded-full text-[10px] font-bold ${
                  i === step ? 'bg-primary text-white' : 'border border-slate-300'
                }`}>
                  {i + 1}
                </span>
              )}
              <span className="hidden sm:inline">Step {i + 1}: {s.title}</span>
            </button>
            {i < STEPS.length - 1 && (
              <div className={`h-[2px] flex-1 mx-4 ${i < step ? 'bg-primary' : 'bg-slate-200'}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step heading */}
      <div>
        <h2 className="text-xl font-bold text-[#052831]">{STEPS[step].fullTitle}</h2>
        <p className="text-sm text-slate-500 mt-1">Step {step + 1} of {STEPS.length}</p>
      </div>

      {/* Step 1: Problem Understanding */}
      {step === 0 && (
        <div className="space-y-6">
          <div>
            <label className={labelCls}>What problem or task would you like AI to help solve? *</label>
            <textarea className={inputCls} rows={3} placeholder="Describe the problem or repetitive task..."
              value={answers.q1 || ''} onChange={e => set('q1', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>How is this currently done? *</label>
            <textarea className={inputCls} rows={3} placeholder="Describe the current process..."
              value={answers.q2 || ''} onChange={e => set('q2', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>How often does this task occur? *</label>
            <select className={inputCls} value={answers.q3 || ''} onChange={e => set('q3', e.target.value)}>
              <option value="">Select frequency...</option>
              {FREQUENCY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Who is impacted by this problem? *</label>
            <CheckboxGroup field="q4" options={IMPACTED_OPTIONS} answers={answers} onToggle={toggleMulti} />
          </div>
          <div>
            <label className={labelCls}>How much time is spent on this per week? *</label>
            <select className={inputCls} value={answers.q5 || ''} onChange={e => set('q5', e.target.value)}>
              <option value="">Select time spent...</option>
              {TIME_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        </div>
      )}

      {/* Step 2: Impact & Value */}
      {step === 1 && (
        <div className="space-y-8">
          <div>
            <label className={labelCls}>What benefits would solving this bring? *</label>
            <CheckboxGroup field="q6" options={BENEFITS_OPTIONS} answers={answers} onToggle={toggleMulti} />
          </div>
          <div>
            <label className={labelCls}>Can you estimate the size of the benefit? <span className={optionalCls}>(optional)</span></label>
            <textarea className={inputCls} rows={3} placeholder="e.g. Reduction of 20 manual hours per week, or potential savings of $50k annually..."
              value={answers.q7 || ''} onChange={e => set('q7', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>How would you rate the potential value? *</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {VALUE_OPTIONS.map(opt => {
                const selected = answers.q8 === opt.value;
                return (
                  <label
                    key={opt.value}
                    className={`relative flex flex-col p-5 rounded-xl border-2 cursor-pointer transition-all ${
                      selected ? 'border-primary bg-primary/5' : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="value_rating"
                      className="absolute top-4 right-4 text-primary focus:ring-primary"
                      checked={selected}
                      onChange={() => set('q8', opt.value)}
                    />
                    <span className="font-bold text-[#052831] text-lg">{opt.label}</span>
                    <span className="text-sm text-slate-500 mt-1">{opt.desc}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Feasibility & Data */}
      {step === 2 && (
        <div className="space-y-6">
          <div>
            <label className={labelCls}>Do digital records of this process exist? *</label>
            <select className={inputCls} value={answers.q9 || ''} onChange={e => set('q9', e.target.value)}>
              <option value="">Select...</option>
              {DIGITAL_RECORDS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Where is the data stored? <span className={optionalCls}>(optional)</span></label>
            <CheckboxGroup field="q10" options={DATA_LOCATION_OPTIONS} answers={answers} onToggle={toggleMulti} />
          </div>
          <div>
            <label className={labelCls}>What types of input would the AI process? *</label>
            <CheckboxGroup field="q11" options={INPUT_TYPES_OPTIONS} answers={answers} onToggle={toggleMulti} />
          </div>
          <div>
            <label className={labelCls}>Does this task require human judgment? <span className={optionalCls}>(optional)</span></label>
            <textarea className={inputCls} rows={3} placeholder="Describe any judgment or decision elements..."
              value={answers.q12 || ''} onChange={e => set('q12', e.target.value)} />
          </div>
        </div>
      )}

      {/* Step 4: Your Idea */}
      {step === 3 && (
        <div className="space-y-6">
          <div>
            <label className={labelCls}>Describe your AI idea *</label>
            <textarea className={inputCls} rows={4} placeholder="What would the AI solution look like? How would it work?"
              value={answers.q13 || ''} onChange={e => set('q13', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Have you seen a similar AI solution elsewhere? <span className={optionalCls}>(optional)</span></label>
            <select className={inputCls} value={answers.q14 || ''} onChange={e => set('q14', e.target.value)}>
              <option value="">Select...</option>
              {SIMILAR_SOLUTION_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Which strategic objectives does this support? <span className={optionalCls}>(optional)</span></label>
            <CheckboxGroup field="q15" options={STRATEGIC_OPTIONS} answers={answers} onToggle={toggleMulti} />
          </div>
          <div className="border-t border-slate-100 pt-6">
            <h3 className="text-base font-bold text-[#052831] mb-4">About You</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Name <span className={optionalCls}>(optional)</span></label>
                <input className={inputCls} type="text" placeholder="Your name"
                  value={answers.submitterName || ''} onChange={e => set('submitterName', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Team / Department <span className={optionalCls}>(optional)</span></label>
                <input className={inputCls} type="text" placeholder="Your team"
                  value={answers.submitterTeam || ''} onChange={e => set('submitterTeam', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email *</label>
                <input className={inputCls} type="email" placeholder="your@email.com"
                  value={answers.submitterEmail || ''} onChange={e => set('submitterEmail', e.target.value)} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation - footer style */}
      <div className="flex items-center justify-between pt-4 border-t border-slate-100">
        <div>
          {step > 0 ? (
            <button onClick={() => setStep(step - 1)}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl border border-slate-300 text-slate-600 font-semibold hover:bg-slate-50 transition-colors text-sm">
              <ChevronLeft size={16} /> Back
            </button>
          ) : (
            <button onClick={onCancel}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl border border-slate-300 text-slate-600 font-semibold hover:bg-slate-50 transition-colors text-sm">
              Cancel
            </button>
          )}
        </div>
        <div>
          {step < STEPS.length - 1 ? (
            <button onClick={() => canAdvance() && setStep(step + 1)} disabled={!canAdvance()}
              className={`flex items-center gap-2 px-8 py-2.5 rounded-xl text-sm font-bold transition-all ${
                canAdvance()
                  ? 'bg-primary text-white hover:bg-primary-dark shadow-lg shadow-primary/20'
                  : 'bg-slate-100 text-slate-400 cursor-not-allowed'
              }`}>
              Next Step <ChevronRight size={16} />
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={!canAdvance() || submitting}
              className={`flex items-center gap-2 px-8 py-2.5 rounded-xl text-sm font-bold transition-all ${
                canAdvance() && !submitting
                  ? 'bg-primary text-white hover:bg-primary-dark shadow-lg shadow-primary/20'
                  : 'bg-slate-100 text-slate-400 cursor-not-allowed'
              }`}>
              {submitting ? <><Loader2 size={16} className="animate-spin" /> Scoring...</> : <><Send size={16} /> Submit Idea</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
